import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  ChevronLeft,
  Users,
  AlertCircle,
  Search,
  Filter,
  X,
  Eye,
  CheckCircle,
  XCircle,
  Minus,
} from 'lucide-react';
import { api, WhyEntitiesResult, PossibleMatchPair } from '../services/api';

export default function PossibleMatchesPage() {
  const [page, setPage] = useState(1);
  const [matchKeyFilter, setMatchKeyFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedPair, setSelectedPair] = useState<PossibleMatchPair | null>(null);
  const limit = 25;

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['possible-match-stats'],
    queryFn: () => api.getPossibleMatchStats(),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['possible-matches', page, limit, matchKeyFilter, searchTerm],
    queryFn: () => api.getPossibleMatches({
      page,
      limit,
      matchKey: matchKeyFilter || undefined,
      search: searchTerm || undefined,
    }),
  });

  const { data: whyData, isLoading: whyLoading } = useQuery({
    queryKey: ['why-entities', selectedPair?.entityId, selectedPair?.relatedEntityId],
    queryFn: () => selectedPair
      ? api.getWhyEntities(selectedPair.entityId, selectedPair.relatedEntityId)
      : Promise.resolve(null),
    enabled: !!selectedPair,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setMatchKeyFilter('');
    setSearchTerm('');
    setSearchInput('');
    setPage(1);
  };

  const hasActiveFilters = matchKeyFilter || searchTerm;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Possible Matches</h1>
        {statsData && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {data?.pagination.total || statsData.totalPossibleMatchPairs} pairs
            {hasActiveFilters && ` (filtered from ${statsData.totalPossibleMatchPairs})`}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card p-4 space-y-4">
        <div className="flex flex-wrap gap-4">
          {/* Search Input */}
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by entity name..."
                className="input pl-10 pr-10"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setSearchTerm('');
                    setPage(1);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </form>

          {/* Match Key Filter */}
          <div className="min-w-[200px]">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={matchKeyFilter}
                onChange={(e) => {
                  setMatchKeyFilter(e.target.value);
                  setPage(1);
                }}
                className="input pl-10 appearance-none cursor-pointer"
              >
                <option value="">All Match Keys</option>
                {statsData && Object.entries(statsData.matchKeyDistribution).map(([key, count]) => (
                  <option key={key} value={key}>
                    {key} ({count})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="btn-secondary flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </button>
          )}
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            {matchKeyFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-sm">
                Match Key: {matchKeyFilter}
                <button onClick={() => { setMatchKeyFilter(''); setPage(1); }}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-sm">
                Search: "{searchTerm}"
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); setPage(1); }}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {!statsLoading && statsData && !hasActiveFilters && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{statsData.totalPossibleMatchPairs}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Possible Match Pairs</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{statsData.totalEntitiesWithPossibleMatches}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Entities Involved</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Match Key Distribution</p>
              <div className="space-y-1">
                {Object.entries(statsData.matchKeyDistribution).slice(0, 4).map(([key, count]) => (
                  <button
                    key={key}
                    onClick={() => { setMatchKeyFilter(key); setPage(1); }}
                    className="w-full flex justify-between text-sm hover:bg-gray-50 dark:hover:bg-gray-700 px-1 rounded"
                  >
                    <span className="text-gray-600 dark:text-gray-400 truncate max-w-[150px]" title={key}>{key}</span>
                    <span className="text-gray-900 dark:text-white font-medium">{count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
          Error loading possible matches. Please try again.
        </div>
      )}

      {/* Possible Matches List */}
      {data?.possibleMatches && data.possibleMatches.length > 0 && (
        <div className="card divide-y divide-gray-200 dark:divide-gray-700">
          {data.possibleMatches.map((match, idx) => (
            <div key={`${match.entityId}-${match.relatedEntityId}-${idx}`} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  {/* Entity 1 */}
                  <Link
                    to={`/entities/${match.entityId}`}
                    className="flex-1 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <p className="font-medium text-gray-900 dark:text-white">
                      {match.entityName || `Entity ${match.entityId}`}
                    </p>
                    <div className="flex gap-2 mt-1">
                      <span className="badge-blue">{match.entityRecordCount} records</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">ID: {match.entityId}</span>
                    </div>
                  </Link>

                  {/* Connection Indicator */}
                  <div className="flex flex-col items-center gap-1 px-2">
                    <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                      <div className="w-6 h-0.5 bg-amber-300 dark:bg-amber-600"></div>
                      <AlertCircle className="h-4 w-4" />
                      <div className="w-6 h-0.5 bg-amber-300 dark:bg-amber-600"></div>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 max-w-[100px] truncate" title={match.matchKey || ''}>
                      {match.matchKey || 'Unknown'}
                    </span>
                  </div>

                  {/* Entity 2 */}
                  <Link
                    to={`/entities/${match.relatedEntityId}`}
                    className="flex-1 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <p className="font-medium text-gray-900 dark:text-white">
                      {match.relatedEntityName || `Entity ${match.relatedEntityId}`}
                    </p>
                    <div className="flex gap-2 mt-1">
                      <span className="badge-blue">{match.relatedRecordCount} records</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">ID: {match.relatedEntityId}</span>
                    </div>
                  </Link>
                </div>

                {/* Why Button */}
                <button
                  onClick={() => setSelectedPair(match)}
                  className="ml-4 p-2 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                  title="View why analysis"
                >
                  <Eye className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {data?.possibleMatches && data.possibleMatches.length === 0 && (
        <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
          {hasActiveFilters
            ? 'No possible matches found matching your filters.'
            : 'No possible matches found. All entities have been uniquely resolved.'}
        </div>
      )}

      {/* Pagination */}
      {data?.pagination && (data.pagination.totalPages ?? 0) > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Page {data.pagination.page} of {data.pagination.totalPages ?? 1} ({data.pagination.total} pairs)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.pagination.totalPages ?? 1, p + 1))}
              disabled={page >= (data.pagination.totalPages ?? 1)}
              className="btn-secondary flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Why Analysis Modal */}
      {selectedPair && (
        <WhyModal
          pair={selectedPair}
          data={whyData}
          isLoading={whyLoading}
          onClose={() => setSelectedPair(null)}
        />
      )}
    </div>
  );
}

interface WhyModalProps {
  pair: PossibleMatchPair;
  data: WhyEntitiesResult | null | undefined;
  isLoading: boolean;
  onClose: () => void;
}

function WhyModal({ pair, data, isLoading, onClose }: WhyModalProps) {
  const getScoreIcon = (bucket: string) => {
    switch (bucket) {
      case 'SAME':
        return <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'CLOSE':
        return <CheckCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />;
      case 'NO_CHANCE':
      case 'DIFFERENT':
        return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
      default:
        return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30';
    if (score >= 70) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30';
    if (score >= 50) return 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30';
    return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Why Analysis</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Comparing {pair.entityName || `Entity ${pair.entityId}`} with{' '}
                {pair.relatedEntityName || `Entity ${pair.relatedEntityId}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)]">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : data ? (
              <div className="space-y-6">
                {/* Match Info */}
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                    <div>
                      <p className="font-medium text-amber-900 dark:text-amber-200">
                        Match Level: {data.matchInfo.matchLevel}
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        Why Key: <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">{data.matchInfo.whyKey}</code>
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        Resolution Rule: {data.matchInfo.resolutionRule}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Feature Comparison Table */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Feature Comparison</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Feature
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            {data.entity1Name}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            {data.entity2Name}
                          </th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Score
                          </th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Result
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {data.featureComparison.map((feature, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              {feature.featureType}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={feature.entity1Value || ''}>
                              {feature.entity1Value || <span className="text-gray-400">N/A</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={feature.entity2Value || ''}>
                              {feature.entity2Value || <span className="text-gray-400">N/A</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getScoreColor(feature.score)}`}>
                                {feature.score}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                {getScoreIcon(feature.scoringBucket)}
                                <span className="text-xs text-gray-500 dark:text-gray-400">{feature.scoringBucket}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Raw Data Comparison */}
                {(data.entity1Record || data.entity2Record) && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Source Record Data</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {data.entity1Record && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                            {data.entity1Record.dataSource} / {data.entity1Record.recordId}
                          </p>
                          <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
                            {JSON.stringify(data.entity1Record.rawData, null, 2)}
                          </pre>
                        </div>
                      )}
                      {data.entity2Record && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                            {data.entity2Record.dataSource} / {data.entity2Record.recordId}
                          </p>
                          <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
                            {JSON.stringify(data.entity2Record.rawData, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                Unable to load why analysis data.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
            <div className="flex gap-2">
              <Link
                to={`/entities/${pair.entityId}`}
                className="btn-secondary text-sm"
                onClick={onClose}
              >
                View Entity {pair.entityId}
              </Link>
              <Link
                to={`/entities/${pair.relatedEntityId}`}
                className="btn-secondary text-sm"
                onClick={onClose}
              >
                View Entity {pair.relatedEntityId}
              </Link>
            </div>
            <button onClick={onClose} className="btn-primary text-sm">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
